import React, { useRef, useEffect, useState } from 'react';
import { fabric } from 'fabric';
import { useNavigate } from "react-router-dom";

const FabricCanvasVisualization = ({ initialBoxData, warehouseId }) => {
    const navigate = useNavigate();
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const objectsRef = useRef([]);
    const tooltipRef = useRef(null);
    let boxData = initialBoxData;
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);

    useEffect(() => {
        const canvas = new fabric.Canvas(canvasRef.current, {
            width: containerRef.current.offsetWidth,
            height: 650,
            hoverCursor: 'pointer',
            selection: false,
            targetFindTolerance: 2
        });

        canvas.controlsAboveOverlay = true; // To ensure controls are always above the objects

        // Function to show tooltip
        const showTooltip = (event, item) => {
            const { offsetX, offsetY } = event.e;
            const tooltip = tooltipRef.current;
            tooltip.style.display = 'block';
            tooltip.style.left = `${offsetX}px`;
            tooltip.style.top = `${offsetY}px`;
            // Construct tooltip content
            tooltip.innerHTML = (`
            <div><strong>Location:</strong> ${item.name}</div>
            <div><strong>Total Part:</strong> ${item.total_part}</div>
            <div><strong>Capacity:</strong> ${item.capacity} pcs</div>
            <div><strong>Occupancy:</strong> ${item.occupancy === null ? 0 : Math.round(item.occupancy)}%</div>
            <br/>
            `);
            // Add button and event listener if tooltip exists
            if (tooltip) {
                tooltip.innerHTML += `<div><button id="showDetailButton">Show Detail</button></div>`;
                // Add click event listener to the button
                const showDetailButton = tooltip.querySelector('#showDetailButton');
                if (showDetailButton) {
                    showDetailButton.addEventListener('click', () => {
                        navigate(`/admin/visualization/detail/${item.id}/${warehouseId}`)
                    });
                }
            }
        };

        // Function to hide tooltip
        const hideTooltip = () => {
            const tooltip = tooltipRef.current;
            if (!isTooltipHovered) {
                tooltip.style.display = 'none';
            }
        };

        if (boxData && Array.isArray(boxData)) {
            boxData.forEach((item, index) => {
                // Create box
                const fillColor = item.occupancy === 0 ? '#5a7684' : item.occupancy >= 100 ? '#f02769' : '#198fed'; // Change color based on threshold
                const rect = new fabric.Rect({
                    left: parseInt(item.x_location),
                    top: parseInt(item.y_location), // Adjust top position to give 16px distance between text and box
                    fill: fillColor,
                    width: item.rack_type === null ? 80 : item.rack_type === 'landscape' ? 80 : 55,
                    height: item.rack_type === null ? 44 : item.rack_type === 'landscape' ? 44 : 80, // Adjust height to compensate for the text distance
                    selectable: false,
                    hasControls: false, // Disable controls for individual objects
                    hoverCursor: 'pointer', // Change cursor when hovering over objects
                    boxData: item // Attach data to the object for reference
                });

                // Create text
                const name = new fabric.Text(item.name, {
                    left: parseInt(item.x_location) + ((item.rack_type === null ? 80 : item.rack_type === 'landscape' ? 80 : 55) / 2), // Position text in the center of the box horizontally
                    top: parseInt(item.y_location) + (item.rack_type === null ? 14 : item.rack_type === 'landscape' ? 14 : 34), // Position text above the box with a margin of 16px
                    fontSize: 14,
                    selectable: false,
                    fill: 'white',
                    textAlign: 'center', // Align text horizontally center
                    originX: 'center',
                    fontWeight: 700,// Set origin of text to center for positioning
                    fontFamily: 'Arial, sans-serif', // Set font family to a default value
                });

                // Group rectangle and text together
                const group = new fabric.Group([rect, name], {
                    selectable: false, // Allow selection of the group
                    hasControls: false, // Disable controls for the group
                    hoverCursor: 'pointer' // Change cursor when hovering over the group
                });

                // Add mouse hover event listeners to the group
                group.on('mouseover', function (event) {
                    showTooltip(event, item);
                });

                group.on('mouseout', function (event) {
                    hideTooltip();
                });

                // Debugging: Listen for object modification event
                group.on('modified', function (event) {
                });

                canvas.add(group); // Add the group to the canvas

                // Store reference to group object
                objectsRef.current[index] = group;
            });
        }

        const handleResize = () => {
            canvas.setWidth(containerRef.current.offsetWidth);
            canvas.renderAll();
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            canvas.dispose();
        };
        // eslint-disable-next-line
    }, [boxData, isTooltipHovered]);

    const handleTooltipMouseEnter = () => {
        setIsTooltipHovered(true);
    };

    const handleTooltipMouseLeave = () => {
        setIsTooltipHovered(false);
    };

    return (
        <div
            ref={containerRef}
            style={{ width: '100%', height: 'auto', position: 'relative', overflow: 'hidden' }}
        >
            <canvas ref={canvasRef} style={{ position: 'absolute', left: 0, top: 0, width: '100%' }} />
            <div
                ref={tooltipRef}
                style={{ position: 'absolute', display: 'none', backgroundColor: 'white', padding: '5px', border: '1px solid black', zIndex: 9999 }}
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
            ></div>
        </div>
    );
};

export default FabricCanvasVisualization;
